import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  addControlErrors,
  removeControlErrors,
} from 'src/app/helpers/forms-utils';
import { FormsValidatorService } from './forms-validator.service';

/**
 * Validator function for a FormGroup that ensures a numeric range between two controls (`minControlName`, `maxControlName`).
 * It validates that:
 * 1. Values are within an optional `minValue` and `maxValue` global range.
 * 2. The maximum value is not less than the minimum value.
 *
 * Clears previous `invalidRangeValue` and `maxLessThanMin` errors before re-validation.
 *
 * @param minControlName The name of the form control for the minimum value.
 * @param maxControlName The name of the form control for the maximum value.
 * @param minValue Optional global minimum boundary.
 * @param maxValue Optional global maximum boundary.
 * @param formsService Optional service for dependency injection (mainly for testing)
 * @returns A ValidatorFn that returns ValidationErrors if the range is invalid, otherwise null.
 */
export function numberRangeValidator(
  minControlName: string,
  maxControlName: string,
  minValue?: number,
  maxValue?: number,
  formsService?: FormsValidatorService
): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const minControl = group.get(minControlName);
    const maxControl = group.get(maxControlName);

    if (!minControl || !maxControl) {
      return null;
    }

    const service = formsService || { addControlErrors, removeControlErrors };

    const errorsToRemove = ['invalidRangeValue', 'maxLessThanMin'];
    service.removeControlErrors(minControl, errorsToRemove);
    service.removeControlErrors(maxControl, errorsToRemove);

    const min = Number(minControl.value);
    const max = Number(maxControl.value);

    const rangeError = validateGlobalRange(
      minControl,
      maxControl,
      min,
      max,
      minValue,
      maxValue,
      service
    );
    if (rangeError) {
      return rangeError;
    }

    if (
      minControl.value === null ||
      maxControl.value === null ||
      isNaN(min) ||
      isNaN(max)
    ) {
      return null;
    }

    const maxLessThanMinError = validateMaxLessThanMin(
      minControl,
      maxControl,
      min,
      max,
      service
    );
    if (maxLessThanMinError) {
      return maxLessThanMinError;
    }

    return null;
  };
}

/**
 * Validates if the min and max values are within an optional global range (minValue, maxValue).
 * @param minControl The control for the minimum value.
 * @param maxControl The control for the maximum value.
 * @param min The numeric value of the minimum control.
 * @param max The numeric value of the maximum control.
 * @param globalMinValue The optional global minimum value.
 * @param globalMaxValue The optional global maximum value.
 * @param service The service to handle control errors
 * @returns ValidationErrors if there's an error, otherwise null.
 */
function validateGlobalRange(
  minControl: AbstractControl,
  maxControl: AbstractControl,
  min: number,
  max: number,
  globalMinValue?: number,
  globalMaxValue?: number,
  service?: { addControlErrors: any; removeControlErrors: any }
): ValidationErrors | null {
  let isMinInvalid = false;
  let isMaxInvalid = false;

  if (globalMinValue !== undefined && min < globalMinValue) {
    isMinInvalid = true;
  }
  if (globalMaxValue !== undefined && min > globalMaxValue) {
    isMinInvalid = true;
  }

  if (globalMinValue !== undefined && max < globalMinValue) {
    isMaxInvalid = true;
  }
  if (globalMaxValue !== undefined && max > globalMaxValue) {
    isMaxInvalid = true;
  }

  if (isMinInvalid || isMaxInvalid) {
    const errorService = service || { addControlErrors, removeControlErrors };
    if (isMinInvalid) {
      errorService.addControlErrors(minControl, { invalidRangeValue: true });
    }
    if (isMaxInvalid) {
      errorService.addControlErrors(maxControl, { invalidRangeValue: true });
    }
    return {
      invalidRangeValue: $localize`:@@rangeErrorMsg:Los valores deben estar entre ${globalMinValue} y ${globalMaxValue}.`,
    };
  }
  return null;
}

/**
 * Validates if the maximum value is less than the minimum value.
 * @param minControl The control for the minimum value.
 * @param maxControl The control for the maximum value.
 * @param min The numeric value of the minimum control.
 * @param max The numeric value of the maximum control.
 * @param service The service to handle control errors
 * @returns ValidationErrors if there's an error, otherwise null.
 */
function validateMaxLessThanMin(
  minControl: AbstractControl,
  maxControl: AbstractControl,
  min: number,
  max: number,
  service?: { addControlErrors: any; removeControlErrors: any }
): ValidationErrors | null {
  if (max < min) {
    const errorService = service || { addControlErrors, removeControlErrors };
    errorService.addControlErrors(maxControl, { maxLessThanMin: true });
    return {
      maxLessThanMin: $localize`:@@maxRangeErrorMsg:El valor máximo no puede ser menor que el mínimo.`,
    };
  } else if (maxControl.hasError('maxLessThanMin')) {
    const { maxLessThanMin, ...otherErrors } = maxControl.errors || {};
    maxControl.setErrors(
      Object.keys(otherErrors).length > 0 ? otherErrors : null
    );
  }
  return null;
}

/**
 * Factory for a validator that checks if the end date is before the start date.
 * Validation is only triggered if both fields have valid Date values.
 * @param startControlName - The name of the start date formControl.
 * @param endControlName - The name of the end date formControl.
 * @returns ValidatorFn
 */
export function dateRangeValidator(
  startControlName: string,
  endControlName: string
): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const startControl = group.get(startControlName);
    const endControl = group.get(endControlName);

    if (!startControl || !endControl) {
      return null;
    }

    const startDate = startControl.value as Date | null;
    const endDate = endControl.value as Date | null;

    if (!startDate || !endDate) {
      clearEndDateBeforeStartDateError(endControl);

      return buildDateParseError(startControl, endControl);
    }

    if (endDate < startDate) {
      const error = {
        endDateBeforeStartDate: $localize`:@@endDateBeforeStartDate:La fecha de finalización no puede ser anterior a la fecha de inicio.`,
      };
      endControl.setErrors({ ...endControl.errors, ...error });
      return error;
    } else {
      clearEndDateBeforeStartDateError(endControl);
    }

    return null;
  };
}

function clearEndDateBeforeStartDateError(endControl: AbstractControl) {
  if (endControl.hasError('endDateBeforeStartDate')) {
    const { endDateBeforeStartDate, ...otherErrors } = endControl.errors || {};
    endControl.setErrors(
      Object.keys(otherErrors).length > 0 ? otherErrors : null
    );
  }
}

function buildDateParseError(
  startControl: AbstractControl,
  endControl: AbstractControl
): ValidationErrors | null {
  if (startControl.hasError('matDatepickerParse')) {
    return {
      matDatepickerParse: $localize`:@@invalidInitialDate:Fecha de inicio no válida.`,
    };
  } else if (endControl.hasError('matDatepickerParse')) {
    return {
      matDatepickerParse: $localize`:@@invalidFinalDate:Fecha final no válida.`,
    };
  }
  return null;
}

/**
 * Minimum length validator that returns a customized error message.
 * @param minLength The required minimum length.
 * @param message The error message to be returned.
 * @returns ValidatorFn
 */
export function minLengthWithMessage(
  minLength: number,
  message = ''
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || minLength <= 0) {
      return null;
    }

    const length = control.value.length;

    if (length < minLength) {
      const defaultMessage = $localize`:@@customMinLengthError:Debe contener al menos ${minLength} caracteres.`;
      const feedbackMessage = message || defaultMessage;
      return {
        customMinLengthError: feedbackMessage,
      };
    }

    return null;
  };
}
